import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MarketingHeader } from '@/components/marketing/MarketingHeader';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { BlogArticle, injectHeadingIds } from '@/components/blog/BlogArticle';
import { BlogAuthor } from '@/components/blog/BlogAuthor';
import { BlogShareButtons } from '@/components/blog/BlogShareButtons';
import { BlogRelatedPosts } from '@/components/blog/BlogRelatedPosts';
import { BlogPostStructuredData } from '@/components/blog/BlogStructuredData';
import { BlogTableOfContents } from '@/components/blog/BlogTableOfContents';
import { useSEO } from '@/hooks/useSEO';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, Check } from 'lucide-react';
import { APP_URL } from '@/data/marketingContent';
import { ARTICLE_RESOURCES } from '@/data/articleResources';
import { absoluteUrl, getPostBySlug, getRelatedPosts, postSeoTitle } from '@/lib/blog';
import { getLoadedPostContent, loadPostContent } from '@/lib/blogContent';
import { countWords, splitSources } from '@/lib/blogSchema';
import { nameToSlug } from '@/lib/blogCategories';
import NotFound from './NotFound';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  const [content, setContent] = useState<{ slug: string; html: string } | null>(null);

  useEffect(() => {
    if (!post || getLoadedPostContent(post.slug) !== undefined) return;
    let cancelled = false;
    loadPostContent(post.slug).then(html => {
      if (!cancelled) setContent({ slug: post.slug, html: html ?? '' });
    });
    return () => {
      cancelled = true;
    };
  }, [post]);

  const pageUrl = `https://beatview.ai/blog/${slug}`;
  const ogImage = post ? absoluteUrl(post.cover) : '';

  useSEO({
    title: `${post ? postSeoTitle(post) : 'Blog Post'} | Beatview`,
    description: post?.seoDescription || '',
    canonicalUrl: pageUrl,
    ogImage,
    ogType: 'article',
    articlePublishedTime: post?.publishedAt,
    articleModifiedTime: post?.updatedAt,
    articleAuthor: post?.author,
  });

  if (!post) return <NotFound />;

  const html = content?.slug === post.slug ? content.html : getLoadedPostContent(post.slug) ?? null;
  const processedContent = html ? injectHeadingIds(html) : '';
  const { main: articleMain, sources: articleSources } = splitSources(processedContent);
  const relatedPosts = getRelatedPosts(post).map(p => ({
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    cover_image_url: p.cover,
    category: p.category,
    author_name: p.author,
    published_at: p.publishedAt,
    reading_time_minutes: p.readingTime,
  }));
  const ctaFeatures = ['Application screening', 'AI video interviews', 'Candidate event log'];
  const categorySlug = nameToSlug.get(post.category) ?? '';
  const articleResource = ARTICLE_RESOURCES[post.slug];

  return (
    <div className="min-h-screen bg-bv-base text-bv-ink">
      <MarketingHeader />
      <BlogPostStructuredData post={post} wordCount={html ? countWords(html) : undefined} />

      <article className="pt-36 pb-16 lg:pt-40">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          {/* Mobile/Tablet TOC */}
          <div className="md:hidden">
            {processedContent && <BlogTableOfContents contentHtml={processedContent} />}
          </div>

          {/* Two-column layout: TOC sidebar + content */}
          <div className="flex gap-10">
            {/* Desktop TOC sidebar */}
            <div className="hidden xl:block w-52 flex-shrink-0 sticky top-28 self-start">
              {processedContent && <BlogTableOfContents contentHtml={processedContent} />}
            </div>

            {/* Main content column */}
            <div className="flex-1 min-w-0 max-w-4xl">
              {/* Breadcrumb */}
              <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 whitespace-nowrap text-sm text-muted-foreground mb-6">
                <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
                <ChevronRight className="h-3 w-3" />
                <Link to={`/blog/${categorySlug}`} className="truncate hover:text-foreground transition-colors">{post.category}</Link>
                <ChevronRight className="hidden h-3 w-3 flex-shrink-0 sm:block" />
                <span className="hidden text-foreground truncate max-w-[200px] sm:inline">{post.title}</span>
              </nav>

              {/* Header */}
              <header className="mb-8">
                <Badge variant="secondary" className="mb-4">{post.category}</Badge>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-5 leading-tight">
                  {post.title}
                </h1>
                <p className="mb-6 max-w-3xl text-lg leading-8 text-muted-foreground">{post.excerpt}</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <BlogAuthor
                    name={post.author}
                    publishedAt={post.publishedAt}
                    updatedAt={post.updatedAt}
                    readingTime={post.readingTime}
                  />
                  <BlogShareButtons title={post.title} url={pageUrl} />
                </div>
              </header>

              {/* Cover Image */}
              <figure className="mb-8 overflow-hidden rounded-2xl border border-border">
                <img
                  src={post.cover}
                  alt={post.coverAlt}
                  className="block aspect-[1200/630] w-full object-cover"
                  width={1200}
                  height={630}
                  {...{ fetchpriority: "high" }}
                  decoding="async"
                />
              </figure>

              {/* Answer-first summary: what search snippets and AI assistants quote */}
              {post.takeaways.length > 0 && (
                <section className="blog-takeaways mb-10 rounded-2xl border border-primary/20 bg-accent/60 p-6" aria-labelledby="key-takeaways">
                  <h2 id="key-takeaways" className="text-sm font-bold uppercase tracking-wider text-primary">Key takeaways</h2>
                  <ul className="mt-3 space-y-2">
                    {post.takeaways.map(t => (
                      <li key={t} className="flex gap-3 text-[15px] leading-7 text-foreground">
                        <Check className="mt-1.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {html === null ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ) : (
                <BlogArticle content={articleMain} />
              )}

              {post.faqs.length > 0 && (
                <div className="blog-article mt-4">
                  <section className="faq-section" aria-labelledby="faq">
                    <h2 id="faq">Frequently asked questions</h2>
                    {post.faqs.map(f => (
                      <div key={f.question} className="faq-item">
                        <h3>{f.question}</h3>
                        <p>{f.answer}</p>
                      </div>
                    ))}
                  </section>
                </div>
              )}

              {articleSources && <BlogArticle content={articleSources} />}

              {articleResource && (
                <section className="mt-12 rounded-xl border border-border bg-muted/30 p-6" aria-labelledby="article-resource-title">
                  <p className="text-sm font-semibold text-primary">Free resource · Version {articleResource.version}</p>
                  <h2 id="article-resource-title" className="mt-2 text-2xl font-bold">{articleResource.heading}</h2>
                  <p className="mt-3 max-w-2xl text-muted-foreground">{articleResource.description}</p>
                  <a href={articleResource.file} download className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90">{articleResource.linkLabel}</a>
                </section>
              )}

              {/* CTA Block */}
              <div className="blog-cta-block mt-16">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      Move an application from screening to interview
                    </h3>
                    <p className="text-sm text-muted-foreground mb-5">
                      Use your configured workflow and review the completed result.
                    </p>
                    <p className="text-muted-foreground mb-6 max-w-lg">
                      Start with one active job for free, or explore how Pro adds AI video interviews.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                      <a
                        href={APP_URL}
                        className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary/90 transition-colors"
                      >
                        Start for free
                      </a>
                      <Link
                        to="/#workflow-demo"
                        className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-semibold border border-border text-foreground hover:bg-muted transition-colors"
                      >
                        See the agent in action
                      </Link>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <ul className="space-y-3">
                      {ctaFeatures.map((feat) => (
                        <li key={feat} className="flex items-center gap-3 text-sm text-foreground">
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10">
                            <Check className="h-3.5 w-3.5 text-primary" />
                          </span>
                          {feat}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Related Posts */}
              <BlogRelatedPosts posts={relatedPosts} />
            </div>
          </div>
        </div>
      </article>

      <MarketingFooter />
    </div>
  );
}
